import { Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Tooltip } from '@/components/Tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getQueryEntity } from '@/sections/insights/queryBuilder/entities';
import type { SimpleFilterDefinition } from '@/sections/insights/queryBuilder/entities/types';

interface Aggregation {
  function: string;
  field: string;
}

const AGGREGATION_FUNCTIONS = [
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
  { value: 'avg', label: 'Avg' },
  { value: 'sum', label: 'Sum' },
];

interface QueryAggregationsProps {
  value: Aggregation[] | undefined;
  editable: boolean;
  rootLabel?: string;
  onChange: (value: Aggregation[] | undefined) => void;
}

export function QueryAggregations({
  value,
  editable,
  rootLabel,
  onChange,
}: QueryAggregationsProps) {
  const tooltipContent =
    'Apply aggregate functions (min, max, avg, sum) to fields within each group';
  const [simpleFilters, setSimpleFilters] = useState<
    SimpleFilterDefinition[] | null
  >(null);

  useEffect(() => {
    let cancelled = false;

    const loadFilters = async () => {
      if (!rootLabel) {
        setSimpleFilters(null);
        return;
      }

      const entity = getQueryEntity(rootLabel);
      const getFilters = entity?.getSimpleFilters;
      if (!getFilters) {
        setSimpleFilters(null);
        return;
      }

      try {
        const definitions = await getFilters();
        if (!cancelled) setSimpleFilters(definitions);
      } catch {
        if (!cancelled) setSimpleFilters(null);
      }
    };
    loadFilters();

    return () => {
      cancelled = true;
    };
  }, [rootLabel]);

  const fieldOptions = useMemo(() => {
    if (!simpleFilters) return [];
    return simpleFilters.map(f => ({ label: f.uiField, value: f.apiField }));
  }, [simpleFilters]);

  const aggregations = value ?? [];

  const handleAdd = () => {
    const firstField = fieldOptions[0]?.value ?? '';
    onChange([...aggregations, { function: 'min', field: firstField }]);
  };

  const handleRemove = (index: number) => {
    const next = aggregations.filter((_, i) => i !== index);
    onChange(next.length > 0 ? next : undefined);
  };

  const handleUpdate = (index: number, patch: Partial<Aggregation>) => {
    const next = aggregations.map((agg, i) =>
      i === index ? { ...agg, ...patch } : agg
    );
    onChange(next);
  };

  const getFieldLabel = (field: string) =>
    fieldOptions.find(o => o.value === field)?.label ?? field;

  if (editable) {
    return (
      <div className="flex items-center gap-2">
        <Tooltip title={tooltipContent}>
          <span className="text-chariot-text-secondary text-sm">
            Aggregations:
          </span>
        </Tooltip>
        <div className="flex flex-wrap items-center gap-1.5">
          {aggregations.map((agg, index) => (
            <div
              key={index}
              className="bg-chariot-background-secondary border-chariot-stroke inline-flex items-center gap-1 rounded-md border px-1 py-0.5"
            >
              <Select
                value={agg.function}
                onValueChange={fn => handleUpdate(index, { function: fn })}
              >
                <SelectTrigger
                  className="h-6 w-16 border-none bg-transparent px-1 text-sm shadow-none"
                  data-testid={`query-header-agg-fn-${index}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AGGREGATION_FUNCTIONS.map(fn => (
                    <SelectItem key={fn.value} value={fn.value}>
                      {fn.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-chariot-text-secondary text-sm">(</span>
              <Select
                value={agg.field}
                onValueChange={field => handleUpdate(index, { field })}
              >
                <SelectTrigger
                  className="h-6 w-28 border-none bg-transparent px-1 text-sm shadow-none"
                  data-testid={`query-header-agg-field-${index}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {fieldOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-chariot-text-secondary text-sm">)</span>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="text-chariot-text-secondary hover:text-chariot-text-primary ml-0.5"
                aria-label="Remove aggregation"
              >
                <X className="size-3" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAdd}
            disabled={fieldOptions.length === 0}
            className="text-chariot-text-secondary hover:text-chariot-text-primary inline-flex items-center gap-1 text-sm disabled:opacity-60"
            data-testid="query-header-agg-add"
          >
            <Plus className="size-3.5" />
            Add
          </button>
        </div>
      </div>
    );
  }

  if (!aggregations.length) return null;

  return (
    <div className="flex items-center gap-2">
      <Tooltip title={tooltipContent}>
        <span className="text-chariot-text-secondary text-sm">
          Aggregations:
        </span>
      </Tooltip>
      <span className="text-chariot-text-primary text-sm">
        {aggregations
          .map(
            agg => `${agg.function.toUpperCase()}(${getFieldLabel(agg.field)})`
          )
          .join(', ')}
      </span>
    </div>
  );
}
